import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import Apexcharts from 'react-apexcharts';
import CountUp from 'react-countup';
import { CircularProgress } from '@material-ui/core';
import constants from '../../../constants';

// CRM Object Type labels
const crmObjectTypeLabels = { agents: 'Agents', deals: 'Deals', contacts: 'Contacts', companies: 'Companies', activities: 'Activities'};

const ChartWidget = ({ widget, data, isLoading, onSettings, onExpand, onDelete, widgetIndex = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const menuRef = useRef(null);
  const contentRef = useRef(null);
  const agents = useSelector(state => state.Agent?.allAgents || []);
  const workspaceUsers = useSelector(state => state.Workspace?.members || []);

  // Use ResizeObserver to track container size changes
  useEffect(() => {
    if (!contentRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });

    resizeObserver.observe(contentRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Helper function to extract a single numeric value from any data format
  const extractNumericValue = (data) => {
    if (data.value !== undefined) return data.value;
    if (data.total !== undefined) return data.total;
    if (data.timeSeries?.length > 0) {
      // Sum all values from time series
      return data.timeSeries.reduce((sum, d) => sum + (d.value || 0), 0);
    }
    if (data.series?.length > 0) {
      // Sum all series values
      if (Array.isArray(data.series[0]?.data)) {
        return data.series.reduce((sum, s) => sum + s.data.reduce((a, b) => a + b, 0), 0);
      }
      return data.series.reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0);
    }
    return 0;
  };

  // Helper function to extract categories/labels from any data format
  const extractCategories = (data) => {
    if (data.categories?.length > 0) return data.categories;
    if (data.labels?.length > 0) return data.labels;
    if (data.timeSeries?.length > 0) return data.timeSeries.map(d => d.date);
    if (data.distribution?.length > 0) return data.distribution.map(d => d.label);
    return [];
  };

  // Helper function to extract series data from any data format
  const extractSeriesData = (data, title) => {
    // If already has properly formatted series for bar/line charts
    if (data.series?.length > 0 && Array.isArray(data.series[0]?.data)) {
      return data.series;
    }
    // If has time series data
    if (data.timeSeries?.length > 0) return [{ name: title, data: data.timeSeries.map(d => d.value || 0)}];
    
    // If has distribution data (from donut-style responses)
    if (data.distribution?.length > 0) return [{  name: title, data: data.distribution.map(d => d.value || 0) }];
    
    // If series is flat array of numbers (donut format)
    if (data.series?.length > 0 && typeof data.series[0] === 'number') return [{  name: title, data: data.series}];
    return [{ name: title, data: [] }];
  };

  // Helper function to extract donut series (flat array of numbers)
  const extractDonutSeries = (data) => {
    // If already flat array of numbers
    if (data.series?.length > 0 && typeof data.series[0] === 'number') return data.series;

    // If has distribution data
    if (data.distribution?.length > 0) return data.distribution.map(d => d.value || 0);

    // If has time series, use values
    if (data.timeSeries?.length > 0) return data.timeSeries.map(d => d.value || 0);

    // If series has data arrays, flatten first series
    if (data.series?.length > 0 && Array.isArray(data.series[0]?.data)) return data.series[0].data;
    return [];
  };

  // Calculate responsive font sizes based on container dimensions
  const getResponsiveSizes = () => {
    const { width, height } = containerSize;
    const w = width || 300;
    const h = height || 200;
    const minDim = Math.min(w, h);

    // More aggressive scaling for better space utilization
    const scale = Math.max(0.7, Math.min(minDim / 150, 1.8));

    return {
      // Donut chart sizes - scale with container
      donutLegend: `${Math.max(9, Math.min(13, Math.round(10 * scale)))}px`,
      donutDataLabel: `${Math.max(8, Math.min(12, Math.round(10 * scale)))}px`,
      donutTotalLabel: `${Math.max(10, Math.min(16, Math.round(12 * scale)))}px`,
      donutTotalValue: `${Math.max(16, Math.min(28, Math.round(22 * scale)))}px`,
      // Donut size percentage - make donut fill more of the container
      donutSize: `${Math.min(80, Math.max(60, 65 + (minDim / 25)))}%`,
      // Bar/Column/Line chart sizes
      axisLabel: `${Math.max(8, Math.min(12, Math.round(10 * scale)))}px`,
      legend: `${Math.max(9, Math.min(13, Math.round(11 * scale)))}px`,
      // Number widget sizes - scale more aggressively
      numberValue: Math.max(46, Math.min(80, Math.round(h * 0.4))),
      numberFormatted: Math.max(11, Math.min(18, Math.round(h * 0.08))),
      // Marker sizes for line charts
      markerSize: Math.max(2, Math.min(6, Math.round(3 * scale))),
      markerHover: Math.max(3, Math.min(8, Math.round(5 * scale))),
      // Bar radius
      barRadius: Math.max(2, Math.min(6, Math.round(3 * scale))),
      // Column width percentage - moderate width for balanced look
      columnWidth: `${Math.min(60, Math.max(35, 40 + (w / 40)))}%`
    };
  };

  const sizes = getResponsiveSizes();

  // Generate chart options based on widget type and data
  const chartConfig = useMemo(() => {
    if (!data || data.error) return null;

    const responsiveSizes = getResponsiveSizes();

    switch (widget.type) {
      case 'number':
        const numValue = extractNumericValue(data);
        return {
          type: 'number',
          value: numValue,
          formatted: data.formatted || numValue?.toString() || '0'
        };

      case 'donut':
        const donutLabels = extractCategories(data);
        const donutSeries = extractDonutSeries(data);
        const donutTotal = donutSeries.reduce((sum, val) => sum + val, 0);
        return {
          type: 'donut',
          options: {
            chart: {
              type: 'donut',
              redrawOnParentResize: true,
              redrawOnWindowResize: true
            },
            labels: donutLabels,
            colors: constants.chartColors.slice(0, donutLabels.length || 6),
            legend: {
              show: true,
              position: 'bottom',
              fontSize: responsiveSizes.donutLegend,
              itemMargin: { horizontal: 8, vertical: 4 },
              offsetY: 0,
              formatter: (seriesName, opts) => {
                const value = opts.w.globals.series[opts.seriesIndex];
                const total = opts.w.globals.series.reduce((a, b) => a + b, 0);
                const percent = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                return `${seriesName} ${value} (${percent}%)`;
              }
            },
            dataLabels: {
              enabled: false
            },
            plotOptions: {
              pie: {
                expandOnClick: false,
                donut: {
                  size: '70%',
                  labels: {
                    show: true,
                    name: {
                      show: true,
                      fontSize: responsiveSizes.donutTotalLabel,
                      offsetY: -6
                    },
                    value: {
                      show: true,
                      fontSize: responsiveSizes.donutTotalValue,
                      fontWeight: 700,
                      offsetY: 6
                    },
                    total: {
                      show: true,
                      label: 'Total',
                      fontSize: responsiveSizes.donutTotalLabel,
                      fontWeight: 500,
                      formatter: () => donutTotal
                    }
                  }
                }
              }
            },
            stroke: { width: 0 }
          },
          series: donutSeries
        };

      case 'column':
      case 'bar':
        const isHorizontal = widget.type === 'bar';
        const barCategories = extractCategories(data);
        const barSeries = extractSeriesData(data, widget.title);
        return {
          type: 'bar',
          options: {
            chart: {
              type: 'bar',
              redrawOnParentResize: true,
              redrawOnWindowResize: true,
              toolbar: { show: false },
              sparkline: { enabled: false }
            },
            plotOptions: {
              bar: {
                horizontal: isHorizontal,
                borderRadius: responsiveSizes.barRadius,
                columnWidth: responsiveSizes.columnWidth,
                barHeight: isHorizontal ? responsiveSizes.columnWidth : undefined,
                distributed: true  // Each bar gets its own color
              }
            },
            colors: constants.chartColors,
            xaxis: {
              categories: barCategories,
              labels: {
                style: { fontSize: responsiveSizes.axisLabel },
                rotate: -45,
                rotateAlways: false,
                trim: true,
                maxHeight: 60
              },
              axisBorder: { show: false },
              axisTicks: { show: false }
            },
            yaxis: {
              labels: {
                style: { fontSize: responsiveSizes.axisLabel }
              }
            },
            dataLabels: { enabled: false },
            legend: { show: false },
            grid: {
              borderColor: '#e7e7e7',
              strokeDashArray: 4,
              padding: { left: 5, right: 5, top: 0, bottom: 0 }
            }
          },
          series: barSeries
        };

      case 'line':
        const lineCategories = extractCategories(data);
        const lineSeries = extractSeriesData(data, widget.title);
        // Generate gradient fill colors based on series colors
        const lineColors = constants.chartColors.slice(0, lineSeries.length || 1);
        return {
          type: 'area',
          options: {
            chart: {
              type: 'area',
              zoom: { enabled: false },
              redrawOnParentResize: true,
              redrawOnWindowResize: true,
              toolbar: { show: false },
              sparkline: { enabled: false },
              parentHeightOffset: 0
            },
            colors: lineColors,
            fill: {
              type: 'gradient',
              gradient: {
                shade: 'light',
                type: 'vertical',
                shadeIntensity: 0.3,
                gradientToColors: lineColors.map(() => '#ffffff'),
                inverseColors: false,
                opacityFrom: 0.5,
                opacityTo: 0.1,
                stops: [0, 100]
              }
            },
            stroke: {
              curve: 'smooth',
              width: 2.5,
              lineCap: 'round'
            },
            xaxis: {
              categories: lineCategories,
              labels: {
                style: {
                  fontSize: responsiveSizes.axisLabel,
                  colors: '#94a3b8',
                  fontWeight: 400
                },
                rotate: 0,
                rotateAlways: false,
                trim: true,
                maxHeight: 40
              },
              axisBorder: { show: false },
              axisTicks: { show: false },
              crosshairs: {
                show: true,
                stroke: {
                  color: '#cbd5e1',
                  width: 1,
                  dashArray: 4
                }
              }
            },
            yaxis: {
              labels: {
                style: {
                  fontSize: responsiveSizes.axisLabel,
                  colors: '#94a3b8',
                  fontWeight: 400
                },
                formatter: (val) => Math.round(val)
              },
              min: 0,
              tickAmount: 4
            },
            dataLabels: { enabled: false },
            legend: {
              show: true,
              position: 'bottom',
              horizontalAlign: 'center',
              fontSize: '12px',
              fontFamily: 'inherit',
              fontWeight: 500,
              offsetY: 8,
              markers: {
                width: 24,
                height: 4,
                radius: 2,
                offsetX: -4,
                offsetY: 0
              },
              itemMargin: {
                horizontal: 20,
                vertical: 8
              }
            },
            grid: {
              show: true,
              borderColor: '#f1f5f9',
              strokeDashArray: 0,
              xaxis: {
                lines: { show: false }
              },
              yaxis: {
                lines: { show: true }
              },
              padding: { left: 10, right: 15, top: 10, bottom: 5 }
            },
            markers: {
              size: 5,
              colors: ['#ffffff'],
              strokeColors: lineColors,
              strokeWidth: 2.5,
              strokeOpacity: 1,
              fillOpacity: 1,
              shape: 'circle',
              hover: {
                size: 7,
                sizeOffset: 3
              }
            },
            tooltip: {
              enabled: true,
              shared: true,
              intersect: false,
              style: {
                fontSize: '12px'
              },
              y: {
                formatter: (val) => val?.toLocaleString() || '0'
              }
            }
          },
          series: lineSeries
        };

      case 'table':
        // Extract table data from various data formats
        const tableRows = [];
        if (data.timeSeries?.length > 0) {
          data.timeSeries.slice(0, 5).forEach(item => {
            tableRows.push({ label: item.date, value: item.value || 0 });
          });
        } else if (data.distribution?.length > 0) {
          data.distribution.slice(0, 5).forEach(item => {
            tableRows.push({ label: item.label, value: item.value || 0 });
          });
        } else if (data.categories?.length > 0 && data.series?.length > 0) {
          const seriesData = Array.isArray(data.series[0]?.data) ? data.series[0].data : data.series;
          data.categories.slice(0, 5).forEach((cat, idx) => {
            tableRows.push({ label: cat, value: seriesData[idx] || 0 });
          });
        } else if (data.labels?.length > 0 && data.series?.length > 0) {
          data.labels.slice(0, 5).forEach((label, idx) => {
            tableRows.push({ label, value: data.series[idx] || 0 });
          });
        }
        const tableTotal = tableRows.reduce((sum, row) => sum + (row.value || 0), 0);
        const maxValue = Math.max(...tableRows.map(r => r.value || 0), 1);
        // Add percentage and bar width to each row
        tableRows.forEach((row, idx) => {
          row.percent = tableTotal > 0 ? ((row.value / tableTotal) * 100).toFixed(1) : 0;
          row.barWidth = maxValue > 0 ? ((row.value / maxValue) * 100) : 0;
          row.rank = idx + 1;
        });
        return {
          type: 'table',
          rows: tableRows,
          total: tableTotal
        };

      default:
        return null;
    }
  }, [widget.type, widget.title, data, containerSize]);

  const getSubtitle = () => {
    // Subtitle shows only the object type label (Agents, Deals, Companies, etc.)
    if (widget.metricCategory === 'crm') {
      return crmObjectTypeLabels[widget.crmObjectType] || 'CRM';
    } else if (widget.metricCategory === 'agent') {
      // Backward compatibility for old 'agent' category
      return 'Agents';
    }
    return 'CRM';
  };

  // Backward compatibility alias
  const getAgentLabel = getSubtitle;

  const renderChart = () => {
    if (isLoading) {
      return (
        <div className="widget-loading">
          <CircularProgress size={24} />
        </div>
      );
    }

    if (!data || data.error) {
      return (
        <div className="widget-error">
          <span>No data available</span>
        </div>
      );
    }

    if (!chartConfig) {
      return (
        <div className="widget-error">
          <span>Invalid chart type</span>
        </div>
      );
    }

    if (chartConfig.type === 'number') {
      return (
        <div className="number-widget-content">
          <div className="number-value" style={{ fontSize: `${sizes.numberValue}px` }}>
            <CountUp
              end={chartConfig.value}
              duration={1}
              separator=","
              preserveValue
            />
          </div>
          {chartConfig.formatted !== chartConfig.value?.toString() && (
            <div className="number-formatted" style={{ fontSize: `${sizes.numberFormatted}px` }}>{chartConfig.formatted}</div>
          )}
        </div>
      );
    }

    if (chartConfig.type === 'table') {
      return (
        <div className="table-widget-content">
          <table className="widget-data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Value</th>
                <th>Percentage (%)</th>
                <th className="th-bar">Distribution</th>
              </tr>
            </thead>
            <tbody>
              {chartConfig.rows.map((row, idx) => (
                <tr key={idx}>
                  <td className="table-label">{row.label}</td>
                  <td className="table-value">{typeof row.value === 'number' ? row.value.toLocaleString() : row.value}</td>
                  <td className="table-percent">{row.percent}%</td>
                  <td className="table-bar-cell">
                    <div className="table-bar-container">
                      <div
                        className="table-bar"
                        style={{
                          width: `${row.barWidth}%`,
                          backgroundColor: constants.chartColors[idx % constants.chartColors.length]
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Use container height for chart - use full height available
    const chartHeight = containerSize.height > 0
      ? Math.max(100, containerSize.height)
      : Math.max(120, ((widget.gridRows || 2) * 80) - 30);

    return (
      <Apexcharts
        options={chartConfig.options}
        series={chartConfig.series}
        type={chartConfig.type}
        height={chartHeight}
        width="100%"
      />
    );
  };

  // Close menu when clicking outside
  const handleMenuToggle = (e) => {
    e.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  const handleMenuAction = (action, e) => {
    e.stopPropagation();
    setMenuOpen(false);
    if (action === 'edit') {
      onSettings(widget);
    } else if (action === 'delete') {
      onDelete(widget.id);
    }
  };

  // Keep showing hover state when menu is open
  const showHoverState = isHovered || menuOpen;

  // Get background color for number widgets
  const getNumberWidgetBackground = () => {
    if (widget.type !== 'number') return null;
    // Use user-selected color if available, otherwise use random based on index
    if (widget.numberColor) return widget.numberColor;
    const colorIndex = widgetIndex % constants.chartColors.length;
    return constants.chartColors[colorIndex];
  };

  const numberBgColor = getNumberWidgetBackground();
  const isNumberWidget = widget.type === 'number';

  return (
    <div
      className={`chart-widget size-${widget.size}${showHoverState ? ' hovered' : ''}${isNumberWidget ? ' number-card' : ''}`}
      style={{
        '--grid-rows': widget.gridRows || 2,
        ...(numberBgColor && { '--number-bg-color': numberBgColor })
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="widget-header">
        <div className="widget-title-area">
          {showHoverState && (
            <span className="drag-handle">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="9" cy="5" r="1.5"></circle>
                <circle cx="9" cy="12" r="1.5"></circle>
                <circle cx="9" cy="19" r="1.5"></circle>
                <circle cx="15" cy="5" r="1.5"></circle>
                <circle cx="15" cy="12" r="1.5"></circle>
                <circle cx="15" cy="19" r="1.5"></circle>
              </svg>
            </span>
          )}
          <div>
            <h1 className="widget-title">{widget.title}</h1>
            <h3 className="widget-subtitle">{getAgentLabel()}</h3>
          </div>
        </div>
        <div className="widget-actions">
          {showHoverState && (
            <>
              <button
                className="action-btn expand-btn"
                onClick={() => onExpand(widget)}
                title="Expand"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <polyline points="9 21 3 21 3 15"></polyline>
                  <line x1="21" y1="3" x2="14" y2="10"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
              </button>
              <div className="widget-menu-container" ref={menuRef}>
                <button
                  className="action-btn menu-btn"
                  onClick={handleMenuToggle}
                  title="Options"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="5" r="2"></circle>
                    <circle cx="12" cy="12" r="2"></circle>
                    <circle cx="12" cy="19" r="2"></circle>
                  </svg>
                </button>
                {menuOpen && (
                  <div className="widget-dropdown-menu">
                    <button className="dropdown-item" onClick={(e) => handleMenuAction('edit', e)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                      <span>Edit</span>
                    </button>
                    <button className="dropdown-item delete" onClick={(e) => handleMenuAction('delete', e)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18"></path>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                      <span>Delete</span>
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <div className="widget-content" ref={contentRef}>
        {renderChart()}
      </div>
    </div>
  );
};

export default ChartWidget;
